// Domain 07 — RouteZero Network Operations Center (NOC), Iloilo City.
//
//   ISP (unmanaged) 203.0.113.33 ── G0/0 .34 ─┐
//                                            R1  NOC core / edge router (2911)
//   NOC LAN 10.70.10.0/24 ── G0/1 .1 ─ SW1 (Vlan1 .2) ─ PC-OPS .11 (Fa0/1) · AP-LOBBY .20 (Fa0/8, third-party AP: LLDP only)
//   SERVERS 10.70.20.0/24 ── G0/2 .1 ─ SW2 (Vlan1 .2) ─ SRV-NTP .30 · SRV-MON .40 (syslog / SNMP NMS) · SRV-TFTP .50 (backups, images)
//   WAN 10.70.12.0/30   ── G0/3 .1 ── G0/0 .2 R2 (Guimaras branch) G0/1 .1 ── BRANCH 10.70.30.0/24 ─ PC-BR .11
//
// Devices are built "factory-new" and then configured by replaying IOS configuration
// text through the real CLI parser, so the running-config, the saved startup-config
// and every TFTP backup are the same text a learner reads and restores.
// No real names, addresses or credentials: documentation ranges and lab-only values.

import { pc, server, router, sw, link, network } from '../../engine/net/model.js';
import { IosSession } from '../../engine/ios/cli.js';
import { configText } from '../../engine/ios/mgmtCli.js';
import { runningConfig } from '../../engine/ios/show.js';
import { attachSystem, imageFile, configFile, imageMd5, IMAGES, MB } from '../../engine/net/system.js';

export const CRED = { enable: 'RzLab-Noc-26', console: 'RzLab-Con-26' };
export const MON = { ip: '10.70.20.40', community: 'RZ-NOC-RO' }; // lab-only community string
export const NTP = '10.70.20.30';
export const TFTP = { ip: '10.70.20.50', root: 'C:\\TFTP-Root' };
export const IMG = {
  old: 'c2900-universalk9-mz.SPA.155-3.M6.bin',
  cur: 'c2900-universalk9-mz.SPA.157-3.M8.bin',
  next: 'c2900-universalk9-mz.SPA.159-3.M9.bin',
  wrong: 'c1900-universalk9-mz.SPA.159-3.M9.bin',
  sw: 'c2960-lanbasek9-mz.150-2.SE4.bin',
};
/** The checksum RouteZero's software repository publishes for the target image. */
export const PUBLISHED_MD5 = imageMd5(IMG.next);
export const ROUTERS = ['R1', 'R2'];
export const SWITCHES = ['SW1', 'SW2'];
export const HOSTS = { 'PC-OPS': '10.70.10.11', 'AP-LOBBY': '10.70.10.20', 'SRV-NTP': '10.70.20.30', 'SRV-MON': '10.70.20.40', 'SRV-TFTP': '10.70.20.50', 'PC-BR': '10.70.30.11' };
const M24 = '255.255.255.0';

const R_PORTS = ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2', 'GigabitEthernet0/3'];
const mkRouter = (id, seq) => router(id, { seq, ports: R_PORTS, hostname: 'Router' });
const mkSwitch = (id, seq) => sw(id, { seq, fastPorts: 8, hostname: 'Switch' });

// ------------------------------------------------------------ as-built configuration text
const COMMON = [
  'service timestamps log datetime msec', 'service password-encryption', `enable secret ${CRED.enable}`,
  'no ip domain-lookup', 'ip domain-name iloilo.routezero.lab',
];
const MONITOR = [
  `ntp server ${NTP}`, `logging host ${MON.ip}`, 'logging trap informational',
  `snmp-server community ${MON.community} ro`, 'snmp-server location Iloilo NOC', 'snmp-server contact noc@routezero.lab',
  `snmp-server host ${MON.ip} version 2c ${MON.community}`, 'snmp-server enable traps',
];
const LINES = [`banner motd #Authorized access only. RouteZero NOC Iloilo.#`, 'line console 0', ` password ${CRED.console}`, ' login', ' exec-timeout 15 0', ' logging synchronous', 'exit'];
export const DESIGN = {
  R1: ['hostname R1', ...COMMON,
    'interface GigabitEthernet0/0', ' description WAN-ISP-CIRCUIT-ILO-4471', ' ip address 203.0.113.34 255.255.255.252', ' no cdp enable', ' no shutdown', 'exit',
    'interface GigabitEthernet0/1', ' description LAN-NOC-SW1-G0/1', ' ip address 10.70.10.1 255.255.255.0', ' no shutdown', 'exit',
    'interface GigabitEthernet0/2', ' description LAN-SERVERS-SW2-G0/1', ' ip address 10.70.20.1 255.255.255.0', ' no shutdown', 'exit',
    'interface GigabitEthernet0/3', ' description WAN-R2-GUIMARAS-G0/0', ' ip address 10.70.12.1 255.255.255.252', ' no shutdown', 'exit',
    'ip route 0.0.0.0 0.0.0.0 203.0.113.33', 'ip route 10.70.30.0 255.255.255.0 10.70.12.2', ...MONITOR, ...LINES],
  R2: ['hostname R2', ...COMMON,
    'interface GigabitEthernet0/0', ' description WAN-R1-ILOILO-G0/3', ' ip address 10.70.12.2 255.255.255.252', ' no shutdown', 'exit',
    'interface GigabitEthernet0/1', ' description LAN-BRANCH-USERS', ' ip address 10.70.30.1 255.255.255.0', ' no shutdown', 'exit',
    'ip route 0.0.0.0 0.0.0.0 10.70.12.1', ...MONITOR, ...LINES],
  SW1: ['hostname SW1', ...COMMON, 'interface FastEthernet0/1', ' description NOC-OPS-CONSOLE', 'exit', 'interface FastEthernet0/8', ' description AP-LOBBY', 'exit',
    'interface GigabitEthernet0/1', ' description UPLINK-R1-G0/1', 'exit', 'interface Vlan1', ' ip address 10.70.10.2 255.255.255.0', ' no shutdown', 'exit', 'ip default-gateway 10.70.10.1', ...LINES],
  SW2: ['hostname SW2', ...COMMON, ...['SRV-NTP', 'SRV-MON', 'SRV-TFTP'].flatMap((h, i) => [`interface FastEthernet0/${i + 1}`, ` description ${h}`, 'exit']),
    'interface GigabitEthernet0/1', ' description UPLINK-R1-G0/2', 'exit', 'interface Vlan1', ' ip address 10.70.20.2 255.255.255.0', ' no shutdown', 'exit', 'ip default-gateway 10.70.20.1', ...LINES],
};

/** Configure a device by typing configuration text into a fresh session. */
export function configure(n, id, lines) {
  const errs = new IosSession(n, id).replayLines(lines);
  if (errs.length) throw new Error(`${id}: ${errs.join(' | ')}`);
  n.devices[id].logs = [];
  return n;
}
/** copy running-config startup-config, silently. */
export function save(n, id) {
  const d = n.devices[id];
  d.startup = runningConfig(n, id).map((l) => (typeof l === 'string' ? l : l.text));
  d.startupState = null;
}
const serverHost = (id, mac, services, extra = {}) => Object.assign(server(id, { ip: HOSTS[id], mask: M24, gw: '10.70.20.1', mac, services }), extra);

/** The tftp server's root folder content. */
export function tftpRoot() {
  return {
    enabled: true, root: TFTP.root, log: [],
    files: {
      [IMG.next]: imageFile(IMG.next, { date: 'Sep 02 2026 14:20:00 +08:00' }),
      [IMG.wrong]: imageFile(IMG.wrong, { date: 'Sep 02 2026 14:22:00 +08:00' }),
    },
  };
}

/**
 * The Iloilo NOC as built: every device configured and saved, R1 still on
 * IOS 15.7(3)M8 with the old 15.5 image still in flash.
 */
export function noc({ configure: doConfig = true } = {}) {
  const R1 = mkRouter('R1', 7); const R2 = mkRouter('R2', 8);
  const SW1 = mkSwitch('SW1', 31); const SW2 = mkSwitch('SW2', 32);
  const ISP = router('ISP', { seq: 9, ports: ['GigabitEthernet0/0'] });
  Object.assign(ISP.interfaces['GigabitEthernet0/0'], { ip: { ip: '203.0.113.33', mask: '255.255.255.252' }, shutdown: false, description: 'TO-ROUTEZERO-ILOILO' });
  ISP.managed = false; ISP.hostname = 'ISP-ILO-PE1';
  const devs = [R1, R2, SW1, SW2, ISP,
    pc('PC-OPS', { ip: HOSTS['PC-OPS'], mask: M24, gw: '10.70.10.1', mac: '0050797a1011', user: 'noc' }),
    Object.assign(pc('AP-LOBBY', { ip: HOSTS['AP-LOBBY'], mask: M24, gw: '10.70.10.1', mac: '0c8ddb1a7020', user: 'admin' }), { os: 'ArcWave AP OS 7.2', lldp: true, lldpCap: 'W', lldpDesc: 'ArcWave AW-640 wireless access point, firmware 7.2.4', portDesc: 'eth0', platform: 'ArcWave AW-640' }),
    serverHost('SRV-NTP', '00155d7d2030', [{ proto: 'udp', port: 123, name: 'ntp' }], { ntpServer: { stratum: 1, refId: '.GPS.' } }),
    serverHost('SRV-MON', '00155d7d2040', [{ proto: 'udp', port: 514, name: 'syslog' }, { proto: 'udp', port: 162, name: 'snmptrap' }, { proto: 'tcp', port: 443, name: 'https' }]),
    serverHost('SRV-TFTP', '00155d7d2050', [{ proto: 'udp', port: 69, name: 'tftp' }], { tftp: tftpRoot() }),
    pc('PC-BR', { ip: HOSTS['PC-BR'], mask: M24, gw: '10.70.30.1', mac: '0050797a3011', user: 'branch' }),
  ];
  const links = [
    link('R1', 'GigabitEthernet0/0', 'ISP', 'GigabitEthernet0/0', { type: 'Cat6 UTP (ISP handoff)' }),
    link('SW1', 'GigabitEthernet0/1', 'R1', 'GigabitEthernet0/1'), link('SW2', 'GigabitEthernet0/1', 'R1', 'GigabitEthernet0/2'),
    link('R1', 'GigabitEthernet0/3', 'R2', 'GigabitEthernet0/0', { type: 'Carrier Ethernet (leased line)' }),
    link('PC-OPS', 'FastEthernet0', 'SW1', 'FastEthernet0/1'), link('AP-LOBBY', 'FastEthernet0', 'SW1', 'FastEthernet0/8'),
    link('SRV-NTP', 'FastEthernet0', 'SW2', 'FastEthernet0/1'), link('SRV-MON', 'FastEthernet0', 'SW2', 'FastEthernet0/2'), link('SRV-TFTP', 'FastEthernet0', 'SW2', 'FastEthernet0/3'),
    link('PC-BR', 'FastEthernet0', 'R2', 'GigabitEthernet0/1'),
  ];
  const n = network(devs, links);
  const factory = { R1: mkRouter('R1', 7), R2: mkRouter('R2', 8), SW1: mkSwitch('SW1', 31), SW2: mkSwitch('SW2', 32) };
  attachSystem(R1, factory.R1, { files: [imageFile(IMG.old, { date: 'Nov 20 2023 09:02:11 +08:00' }), imageFile(IMG.cur)], image: IMG.cur, boot: [`flash:${IMG.cur}`] });
  attachSystem(R2, factory.R2, { files: [imageFile(IMG.cur)], image: IMG.cur, boot: [`flash:${IMG.cur}`] });
  for (const s of [SW1, SW2]) attachSystem(s, factory[s.id], { files: [imageFile(IMG.sw, { date: 'Mar 01 1993 00:04:12 +08:00' })], image: IMG.sw, flashSize: 64 * MB });
  R1.serial = 'FTX1840A1BC'; R2.serial = 'FTX1840A2DE'; SW1.serial = 'FOC1932X0AB'; SW2.serial = 'FOC1932X0CD';
  if (doConfig) for (const id of [...ROUTERS, ...SWITCHES]) { configure(n, id, DESIGN[id]); save(n, id); }
  return n;
}
/** Build the network and apply presets in order. */
export function nocWith(...steps) {
  const n = noc();
  for (const f of steps) f(n);
  return n;
}
/** Run configuration lines on a device of a built network (and save unless told not to). */
export const apply = (id, lines, { persist = true } = {}) => (n) => { configure(n, id, lines); if (persist) save(n, id); return n; };
/** CHG-7301 done: R1 runs 15.9(3)M9 with 15.7(3)M8 as the fallback; the 15.5 image is gone. */
export function upgraded(n) {
  const R1 = n.devices.R1;
  R1.sys.flash.files = [imageFile(IMG.cur), imageFile(IMG.next, { date: 'Oct 08 2026 23:12:40 +08:00' })];
  configure(n, 'R1', ['no boot system', `boot system flash:${IMG.next}`, `boot system flash:${IMG.cur}`]);
  R1.sys.image = { file: IMG.next, version: IMAGES[IMG.next].version, platform: IMAGES[IMG.next].platform };
  save(n, 'R1');
  return n;
}
/** CHG-7288: the branch voice VLAN gateway on R2 (Loopback31 stands in for it) and R1's route to it. */
export function voiceVlan(n) {
  configure(n, 'R2', ['interface Loopback31', ' description BRANCH-VOICE-GW', ' ip address 10.70.31.1 255.255.255.0']);
  configure(n, 'R1', ['ip route 10.70.31.0 255.255.255.0 10.70.12.2']);
  save(n, 'R2'); save(n, 'R1');
  return n;
}
/** Last night's backups on SRV-TFTP (current at build time). */
export function withBackups(n, ids = [...ROUTERS, ...SWITCHES]) {
  for (const id of ids) n.devices['SRV-TFTP'].tftp.files[`${id.toLowerCase()}-confg`] = { ...backupFile(n, id, `${id.toLowerCase()}-confg`), from: id };
  return n;
}
/** The operational baseline used from Mission 07 on. */
export const operational = () => nocWith(upgraded, voiceVlan);
/** A config file as it would sit on the TFTP server. */
export const backupFile = (n, id, name) => configFile(name, configText(n, id).filter((l) => l !== ''), 'Oct 07 2026 22:00:05 +08:00');
export { IMAGES, imageMd5 };

// ------------------------------------------------------------ diagram
const NODE = {
  ISP: { x: 140, y: 70, type: 'cloud', sub: 'ISP-ILO-PE1 · 203.0.113.33' },
  R1: { x: 470, y: 200, type: 'router', sub: 'NOC core/edge · 2911' },
  R2: { x: 840, y: 200, type: 'router', sub: 'Guimaras branch' },
  'PC-BR': { x: 840, y: 380, type: 'pc', sub: 'Branch 10.70.30.11' },
  SW1: { x: 230, y: 360, type: 'switch', sub: 'NOC access · Vlan1 .2' },
  'PC-OPS': { x: 110, y: 510, type: 'pc', sub: 'Operator 10.70.10.11' },
  'AP-LOBBY': { x: 330, y: 510, type: 'pc', sub: 'Vendor AP .20 (LLDP)' },
  SW2: { x: 590, y: 360, type: 'switch', sub: 'Servers · Vlan1 .2' },
  'SRV-NTP': { x: 470, y: 510, type: 'server', sub: 'NTP .30 (stratum 1)' },
  'SRV-MON': { x: 600, y: 510, type: 'server', sub: 'Syslog/SNMP .40' },
  'SRV-TFTP': { x: 730, y: 510, type: 'server', sub: 'TFTP .50' },
};
const LABELS = {
  'R1:GigabitEthernet0/0': 'G0/0 .34', 'ISP:GigabitEthernet0/0': '.33', 'R1:GigabitEthernet0/1': 'G0/1 .1', 'SW1:GigabitEthernet0/1': 'G0/1',
  'R1:GigabitEthernet0/2': 'G0/2 .1', 'SW2:GigabitEthernet0/1': 'G0/1', 'R1:GigabitEthernet0/3': 'G0/3 .1', 'R2:GigabitEthernet0/0': 'G0/0 .2',
  'R2:GigabitEthernet0/1': 'G0/1 .1', 'SW1:FastEthernet0/1': 'Fa0/1', 'SW1:FastEthernet0/8': 'Fa0/8',
  'SW2:FastEthernet0/1': 'Fa0/1', 'SW2:FastEthernet0/2': 'Fa0/2', 'SW2:FastEthernet0/3': 'Fa0/3',
};
export function nocLayout(n, { title, notes = {} } = {}) {
  return {
    width: 1000, height: 600, title: title || 'RouteZero NOC — Iloilo City', nodes: Object.fromEntries(Object.entries(NODE).filter(([id]) => n.devices[id])),
    zones: [
      { x: 20, y: 10, w: 300, h: 110, label: 'INTERNET · edge link 203.0.113.32/30', cloud: true },
      { x: 20, y: 300, w: 380, h: 280, label: 'NOC LAN · 10.70.10.0/24 · gateway .1' },
      { x: 420, y: 300, w: 360, h: 280, label: 'SERVERS · 10.70.20.0/24 · gateway .1' },
      { x: 790, y: 130, w: 195, h: 320, label: 'GUIMARAS · WAN 10.70.12.0/30 · LAN 10.70.30.0/24' },
    ],
    linkLabels: LABELS,
    linkNotes: { 'R1:GigabitEthernet0/3--R2:GigabitEthernet0/0': '10.70.12.0/30', 'R1:GigabitEthernet0/0--ISP:GigabitEthernet0/0': '203.0.113.32/30', ...notes },
    overlays: [],
  };
}
